import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, Save, ExternalLink, AlertCircle, Copy } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const INITIAL_SENDER = { sender_name: "AI Elite Mastery", sender_domain: "aielitegroup.com", sender_email: "account@aielitegroup.com", reply_to: "" };
const fields = [
  ["sender_name", "Sender name", "text", "The business name recipients will see.", 120],
  ["sender_domain", "Sender domain", "text", "Use the exact domain verified in Resend, without https:// or a path.", 253],
  ["sender_email", "Sender email", "email", "The address after @ must match the sender domain above.", 254],
  ["reply_to", "Reply-to email (optional)", "email", "An inbox your team monitors. It may use a different domain.", 254],
];

const SenderForm = ({ form, setForm, saved, saving, error, onSave, onReload }) => {
  const domainMismatch = form.sender_email.includes("@") && form.sender_email.trim().toLowerCase().split("@").pop() !== form.sender_domain.trim().toLowerCase();
  return <section className="bg-white border border-slate-200 rounded-md shadow-sm p-6" data-testid="email-sender-section">
    <h2 className="font-heading font-bold text-base md:text-lg" data-testid="email-sender-heading">Sender identity</h2>
    <p className="text-sm text-slate-500 mt-1 mb-6" data-testid="email-sender-description">Save your preferred sender. The sender email must be on a domain you have verified in your Resend account.</p>
    <form onSubmit={onSave} className="space-y-5" data-testid="email-settings-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{fields.map(([key, label, type, hint, maxLength]) => <div key={key} className="space-y-2">
        <Label htmlFor={`email-${key}`}>{label}</Label>
        <Input id={`email-${key}`} type={type} maxLength={maxLength} required={key !== "reply_to"} value={form[key]} disabled={saving} onChange={(e) => setForm({ ...form, [key]: e.target.value })} aria-describedby={`email-${key}-hint`} aria-invalid={key === "sender_email" && domainMismatch ? true : undefined} className={key === "sender_name" ? "" : "font-mono text-sm"} data-testid={`email-${key}-input`} />
        <p id={`email-${key}-hint`} className="text-xs text-slate-500" data-testid={`email-${key}-hint`}>{hint}</p>
      </div>)}</div>
      {domainMismatch && <p role="alert" className="text-sm text-rose-700" data-testid="email-domain-mismatch">Sender email must use the configured sender domain exactly.</p>}
      {error && <p role="alert" className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="email-save-error">{error}</p>}
      <div className="flex flex-wrap items-center gap-3 border-t pt-5">
        <Button type="submit" disabled={saving || domainMismatch} data-testid="email-save-button"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save settings"}</Button>
        <Button type="button" variant="outline" disabled={saving} onClick={onReload} data-testid="email-reload-button">Reload saved settings</Button>
        <span role="status" className="text-xs text-slate-500" data-testid="email-save-status">{saved ? `Saved ${fmtDateTime(saved.updated_at)} · Revision ${saved.revision}` : "Not saved yet"}</span>
      </div>
    </form>
  </section>;
};

const EVENT_COLOR = {
  "email.sent": "text-slate-600",
  "email.delivered": "text-emerald-700",
  "email.opened": "text-blue-700",
  "email.clicked": "text-blue-700",
  "email.bounced": "text-rose-700",
  "email.complained": "text-rose-700",
  "email.delivery_delayed": "text-amber-700",
  "email.failed": "text-rose-700",
};

const DeliverabilityPanel = ({ data, loading, onRefresh, webhookUrl, secretStatus, onSaveSecret, onClearSecret, savingSecret }) => {
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(webhookUrl); toast.success("Webhook URL copied"); }
    catch { toast.error("Could not copy — please copy manually"); }
  };
  const [secretInput, setSecretInput] = React.useState("");
  const [showSecret, setShowSecret] = React.useState(false);
  const counts = data?.sent_counts || { invoices: 0, proformas: 0, receipts: 0, total: 0 };
  const events = data?.event_counts || {};
  const source = secretStatus?.source || "none";
  return <section className="bg-white border border-slate-200 rounded-md p-6 space-y-5" data-testid="deliverability-panel">
    <div className="flex items-center justify-between">
      <h2 className="font-heading font-bold text-base md:text-lg" data-testid="deliverability-heading">Deliverability · last 30 days</h2>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="deliverability-refresh">{loading ? "Refreshing…" : "Refresh"}</Button>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="deliverability-sent-counts">
      <StatCard label="Total sent" value={counts.total} testId="stat-total" />
      <StatCard label="Invoices" value={counts.invoices} testId="stat-invoices" />
      <StatCard label="Pro-formas" value={counts.proformas} testId="stat-proformas" />
      <StatCard label="Receipts" value={counts.receipts} testId="stat-receipts" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard label="Delivered" value={events["email.delivered"] || 0} tone="emerald" testId="stat-delivered" />
      <StatCard label="Bounced" value={events["email.bounced"] || 0} tone="rose" testId="stat-bounced" />
      <StatCard label="Complained" value={events["email.complained"] || 0} tone="rose" testId="stat-complained" />
      <StatCard label="Opened" value={events["email.opened"] || 0} tone="blue" testId="stat-opened" />
    </div>

    <div className="border-t pt-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">Resend webhook</p>
        <Badge variant="outline" className={data?.webhook_ready ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"} data-testid="deliverability-webhook-status">
          {data?.webhook_ready ? (source === "database" ? "Signing secret set (in app)" : "Signing secret set (env)") : "Signing secret missing"}
        </Badge>
      </div>
      <p className="text-xs text-slate-600">Configure this endpoint in your Resend dashboard → Webhooks. Bounces, complaints and delivered events will appear here.</p>
      <div className="flex items-stretch gap-2">
        <Input value={webhookUrl} readOnly className="font-mono text-xs" data-testid="deliverability-webhook-url" onFocus={(e) => e.target.select()} />
        <Button variant="outline" size="sm" onClick={copyUrl} data-testid="deliverability-webhook-copy"><Copy className="w-4 h-4" /></Button>
      </div>

      <div className="rounded border border-slate-200 bg-slate-50 p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold" data-testid="webhook-secret-heading">Signing secret</p>
            <p className="text-xs text-slate-600 mt-0.5">
              After creating the webhook in Resend, copy its <strong>Signing secret</strong> (starts with <code className="font-mono">whsec_</code>) and paste it below.
            </p>
          </div>
          {secretStatus?.has_secret && (
            <Badge variant="outline" className="border-slate-200 bg-white text-slate-700 font-mono text-xs" data-testid="webhook-secret-masked">
              {secretStatus.masked || (secretStatus.source === "environment" ? "env" : "•••")}
            </Badge>
          )}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (!secretInput.trim()) return; onSaveSecret(secretInput.trim()).then((ok) => { if (ok) { setSecretInput(""); setShowSecret(false); } }); }} className="flex flex-col sm:flex-row gap-2">
          <Input
            type={showSecret ? "text" : "password"}
            value={secretInput}
            onChange={(e) => setSecretInput(e.target.value)}
            placeholder="whsec_..."
            autoComplete="off"
            spellCheck={false}
            className="font-mono text-xs"
            data-testid="webhook-secret-input"
          />
          <label className="flex items-center gap-1 text-xs text-slate-600 whitespace-nowrap px-2">
            <input type="checkbox" checked={showSecret} onChange={(e) => setShowSecret(e.target.checked)} data-testid="webhook-secret-show" />
            Show
          </label>
          <Button type="submit" size="sm" disabled={savingSecret || !secretInput.trim()} data-testid="webhook-secret-save">{savingSecret ? "Saving…" : "Save secret"}</Button>
          {secretStatus?.source === "database" && (
            <Button type="button" size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" disabled={savingSecret} onClick={() => { if (window.confirm("Clear the stored signing secret? Resend webhooks will fail until you save a new one (or set RESEND_WEBHOOK_SECRET in env).")) onClearSecret(); }} data-testid="webhook-secret-clear">Clear</Button>
          )}
        </form>
        {secretStatus?.updated_at && (
          <p className="text-[11px] text-slate-500" data-testid="webhook-secret-updated">
            Last saved {fmtDateTime(secretStatus.updated_at)}
          </p>
        )}
        {source === "environment" && (
          <p className="text-[11px] text-amber-800" data-testid="webhook-secret-env-note">
            Currently using <code className="font-mono">RESEND_WEBHOOK_SECRET</code> from the environment. Saving here will take precedence.
          </p>
        )}
      </div>
    </div>

    <div className="border-t pt-4">
      <p className="text-sm font-semibold mb-2">Latest webhook events</p>
      {(data?.recent_events || []).length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="deliverability-no-events">No webhook events yet. Send an email or verify the webhook is reaching this app.</p>
      ) : (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs" data-testid="deliverability-events-table">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
              <tr><th className="text-left px-3 py-1.5">Event</th><th className="text-left px-3 py-1.5">To</th><th className="text-left px-3 py-1.5">Received</th></tr>
            </thead>
            <tbody>
              {(data?.recent_events || []).map((ev) => (
                <tr key={ev.id} className="border-t border-slate-100" data-testid={`deliverability-event-${ev.id}`}>
                  <td className={`px-3 py-1.5 font-mono ${EVENT_COLOR[ev.event_type] || "text-slate-700"}`}>{ev.event_type}</td>
                  <td className="px-3 py-1.5 font-mono">{ev.to || "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{fmtDateTime(ev.received_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </section>;
};

const StatCard = ({ label, value, tone = "slate", testId }) => {
  const toneClass = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
  }[tone];
  return <div className={`border rounded p-3 ${toneClass}`} data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest opacity-75">{label}</div>
    <div className="font-mono text-2xl tabular-nums mt-1">{value}</div>
  </div>;
};

const STATUS_BADGE = {
  ready: "border-emerald-200 bg-emerald-50 text-emerald-800",
  missing_key: "border-rose-200 bg-rose-50 text-rose-800",
  no_sender: "border-amber-200 bg-amber-50 text-amber-800",
};
const STATUS_LABEL = { ready: "Connected", missing_key: "Not connected", no_sender: "Sender not saved" };

const DeliveryStatus = ({ recipient, status, message, onTest, testing, testResult }) => {
  const ready = status === "ready";
  return <aside className="bg-white border border-slate-200 rounded-md p-6 self-start space-y-5" data-testid="email-delivery-section">
    <div className="flex items-center justify-between gap-2"><h2 className="font-heading font-bold text-base md:text-lg" data-testid="email-delivery-heading">Delivery & test</h2><Badge variant="outline" className={STATUS_BADGE[status] || STATUS_BADGE.missing_key} data-testid="email-delivery-status">{STATUS_LABEL[status] || "Not connected"}</Badge></div>
    <p className="text-sm text-slate-600" data-testid="email-delivery-description">{message}</p>
    <div className="border-t pt-4"><p className="text-xs text-slate-500 mb-1">Test recipient · signed-in Superadmin</p><p className="font-mono text-sm break-all" data-testid="email-test-recipient">{recipient}</p></div>
    <Button disabled={!ready || testing} onClick={onTest} className="w-full" data-testid="email-test-button"><Mail className="w-4 h-4 mr-2" />{testing ? "Sending…" : "Send test email"}</Button>
    {testResult?.ok && <p className="text-xs text-emerald-800 border border-emerald-200 bg-emerald-50 p-2 rounded" data-testid="email-test-success">Test email accepted by Resend (id {testResult.email_id}). Check your inbox and spam.</p>}
    {testResult?.error && <p role="alert" className="text-xs text-rose-800 border border-rose-200 bg-rose-50 p-2 rounded" data-testid="email-test-error">{testResult.error}</p>}
    {!ready && <p className="text-xs text-slate-500" data-testid="email-test-unavailable">{status === "missing_key" ? "Add RESEND_API_KEY in Manage App → Secrets and restart the backend, then reload this page." : "Save the sender identity first."}</p>}
    <p className="border-t pt-4 text-xs text-slate-500" data-testid="email-document-status">{ready ? "Invoices, receipts and pro-formas can now be emailed with their PDF attachment." : "Invoice, receipt and pro-forma email delivery becomes available once Resend is connected."}</p>
  </aside>;
};

const EmailSetupGuide = ({ domain }) => <section className="bg-white border border-slate-200 rounded-md p-6" data-testid="email-setup-guide">
  <h2 className="font-heading font-bold text-base md:text-lg" data-testid="email-guide-heading">Domain & email setup guide</h2>
  <p className="text-sm text-slate-500 mt-1" data-testid="email-guide-intro">Complete domain setup in your Resend account. The app does not check DNS or domain verification yet.</p>
  <Accordion type="multiple" defaultValue={["domain"]}>
    <AccordionItem value="domain"><AccordionTrigger data-testid="email-guide-domain-trigger">1. Add your sending domain</AccordionTrigger><AccordionContent data-testid="email-guide-domain-content">
      <p className="text-slate-600">In Resend → Domains, add <strong className="break-all">{domain || "your sending domain"}</strong>. To send as account@aielitegroup.com, verify aielitegroup.com. If you later choose a subdomain, verify that exact subdomain and update both sender fields here.</p>
      <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-blue-700 underline" data-testid="email-guide-domains-link">Open Resend Domains <ExternalLink className="w-3 h-3" aria-hidden="true" /></a>
    </AccordionContent></AccordionItem>
    <AccordionItem value="dns"><AccordionTrigger data-testid="email-guide-dns-trigger">2. Add the DNS records Resend provides</AccordionTrigger><AccordionContent data-testid="email-guide-dns-content">
      <p className="text-slate-600">At your domain’s DNS provider, copy each record’s type, host and value exactly as shown in Resend, including the required DKIM and SPF records. Do not invent values or replace your existing inbox’s MX records. If a record already exists at the same host, follow Resend’s guidance before editing it.</p>
      <p className="text-slate-600 mt-2">Select Verify in Resend and wait until the domain shows Verified. DNS updates may take time. DMARC is also recommended; coordinate its policy with whoever manages your company email.</p>
      <a href="https://resend.com/docs/dashboard/domains/introduction" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-blue-700 underline" data-testid="email-guide-dns-link">Read Resend’s domain guide <ExternalLink className="w-3 h-3" aria-hidden="true" /></a>
    </AccordionContent></AccordionItem>
    <AccordionItem value="secrets"><AccordionTrigger data-testid="email-guide-secrets-trigger">3. Keep the API key in Secrets</AccordionTrigger><AccordionContent data-testid="email-guide-secrets-content">
      <p className="text-slate-600">Your requested credential is the server-side <code className="font-mono">RESEND_API_KEY</code> secret. Do not paste it into sender fields, source code or the database. This settings page does not read, display, validate or replace that key.</p>
      <p className="text-amber-800 mt-2">Having a valid key and verified domain does not enable sending in this build. The direct email connection still needs implementation.</p>
    </AccordionContent></AccordionItem>
    <AccordionItem value="sender"><AccordionTrigger data-testid="email-guide-sender-trigger">4. Save the sender and reply-to</AccordionTrigger><AccordionContent data-testid="email-guide-sender-content">
      <p className="text-slate-600">Use your business name and one email address on the verified sender domain. Reply-to is optional and should be an inbox your team monitors. Domain verification does not create an inbox. You can change the domain and email here later; verify the new domain in Resend first.</p>
    </AccordionContent></AccordionItem>
    <AccordionItem value="test"><AccordionTrigger data-testid="email-guide-test-trigger">5. Test before sending documents</AccordionTrigger><AccordionContent data-testid="email-guide-test-content">
      <p className="text-slate-600">Once delivery is connected, send a test to the signed-in Superadmin and check the sender, reply-to, inbox and spam folder. Then verify an invoice, receipt and pro-forma email with its PDF. A provider’s acceptance means queued for delivery, not confirmation that the recipient received it.</p>
      <p className="text-amber-800 mt-2">Testing is currently disabled. No successful send or verified delivery is claimed.</p>
    </AccordionContent></AccordionItem>
  </Accordion>
</section>;

export default function EmailSettings() {
  const { user } = useAuth();
  const allowed = user?.role === "superadmin";
  const [form, setForm] = useState(INITIAL_SENDER), [saved, setSaved] = useState(null), [recipient, setRecipient] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState("missing_key"), [deliveryMessage, setDeliveryMessage] = useState("");
  const [testing, setTesting] = useState(false), [testResult, setTestResult] = useState(null);
  const [deliverability, setDeliverability] = useState(null), [deliverabilityLoading, setDeliverabilityLoading] = useState(false);
  const [secretStatus, setSecretStatus] = useState(null), [savingSecret, setSavingSecret] = useState(false);
  const [loading, setLoading] = useState(true), [loadError, setLoadError] = useState(""), [error, setError] = useState(""), [saving, setSaving] = useState(false), [reload, setReload] = useState(0);
  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const webhookUrl = `${backendBase}/api/email-events/resend`;

  const loadDeliverability = React.useCallback(async () => {
    setDeliverabilityLoading(true);
    try {
      const [{ data }, secretResp] = await Promise.all([
        api.get("/settings/email/deliverability"),
        api.get("/settings/email/webhook-secret").catch(() => ({ data: null })),
      ]);
      setDeliverability(data);
      if (secretResp?.data) setSecretStatus(secretResp.data);
    } catch (e) {
      // Non-fatal: the top card already shows connection status; log for diagnostics.
      console.warn("EmailSettings: deliverability/webhook-secret fetch failed", e);
    } finally { setDeliverabilityLoading(false); }
  }, []);

  const saveWebhookSecret = async (secret) => {
    setSavingSecret(true);
    try {
      const { data } = await api.put("/settings/email/webhook-secret", { secret });
      setSecretStatus(data);
      toast.success("Signing secret saved");
      loadDeliverability();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save signing secret");
      return false;
    } finally { setSavingSecret(false); }
  };
  const clearWebhookSecret = async () => {
    setSavingSecret(true);
    try {
      await api.delete("/settings/email/webhook-secret");
      toast.success("Signing secret cleared");
      loadDeliverability();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to clear secret");
    } finally { setSavingSecret(false); }
  };

  useEffect(() => { if (allowed) loadDeliverability(); }, [allowed, reload, loadDeliverability]);
  useEffect(() => {
    if (!allowed) return;
    const c = new AbortController(); setLoading(true); setLoadError(""); setError("");
    api.get("/settings/email", { signal: c.signal }).then(({ data }) => {
      setSaved(data.settings); setRecipient(data.test_recipient);
      setDeliveryStatus(data.delivery_status); setDeliveryMessage(data.delivery_message);
      setForm(data.settings ? Object.fromEntries(fields.map(([key]) => [key, data.settings[key] || ""])) : INITIAL_SENDER);
    }).catch((e) => { if (!c.signal.aborted) setLoadError(formatApiError(e.response?.data?.detail)); }).finally(() => { if (!c.signal.aborted) setLoading(false); });
    return () => c.abort();
  }, [allowed, reload]);
  const save = async (event) => {
    event.preventDefault(); setSaving(true); setError("");
    try {
      const { data } = await api.put("/settings/email", { ...form, reply_to: form.reply_to.trim() || null, expected_revision: saved?.revision || 0 });
      setSaved(data); setForm(Object.fromEntries(fields.map(([key]) => [key, data[key] || ""])));
      toast.success("Sender preferences saved.");
      setReload((r) => r + 1);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/settings/email/test");
      setTestResult({ ok: true, email_id: data.email_id });
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === "object" ? detail?.message : formatApiError(detail);
      setTestResult({ error: message || e.message });
      toast.error(message || e.message);
    } finally { setTesting(false); }
  };
  if (!allowed) return <PageBody><p role="alert" className="border border-rose-200 bg-rose-50 text-rose-800 rounded p-4" data-testid="email-settings-forbidden">Email Settings is restricted to Superadmin.</p></PageBody>;
  const bannerTone = deliveryStatus === "ready" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : deliveryStatus === "missing_key" ? "border-rose-200 bg-rose-50 text-rose-900" : "border-amber-200 bg-amber-50 text-amber-900";
  const bannerText = deliveryStatus === "ready"
    ? <><strong>Resend is connected.</strong> Test emails and document emails will use your saved sender.</>
    : deliveryStatus === "missing_key"
      ? <><strong>RESEND_API_KEY is missing.</strong> Add it in Manage App → Secrets and restart the backend to enable sending.</>
      : <><strong>Save your sender identity to finish connecting Resend.</strong> {deliveryMessage}</>;
  return <>
    <PageHeader title="Email Settings" subtitle="Sender preferences and domain setup · Superadmin only" />
    <PageBody className="space-y-6">
      <div role="status" className={`flex items-start gap-3 rounded-md border p-4 text-sm ${bannerTone}`} data-testid="email-connection-notice"><AlertCircle className="w-5 h-5 shrink-0" aria-hidden="true" /><p>{bannerText}</p></div>
      {loading ? <p role="status" data-testid="email-settings-loading">Loading sender preferences…</p> : loadError ? <div role="alert" className="space-y-3 text-rose-800" data-testid="email-load-error"><p>{loadError}</p><Button variant="outline" onClick={() => setReload((r) => r + 1)} data-testid="email-load-retry">Try again</Button></div> : <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2 space-y-6"><SenderForm form={form} setForm={setForm} saved={saved} saving={saving} error={error} onSave={save} onReload={() => { if (window.confirm("Reload saved settings? Unsaved edits will be discarded.")) setReload((r) => r + 1); }} /><DeliverabilityPanel data={deliverability} loading={deliverabilityLoading} onRefresh={loadDeliverability} webhookUrl={webhookUrl} secretStatus={secretStatus} onSaveSecret={saveWebhookSecret} onClearSecret={clearWebhookSecret} savingSecret={savingSecret} /><EmailSetupGuide domain={form.sender_domain} /></div>
        <DeliveryStatus recipient={recipient} status={deliveryStatus} message={deliveryMessage} onTest={runTest} testing={testing} testResult={testResult} />
      </div>}
    </PageBody>
  </>;
}
